import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, CardBody, CardTitle, Badge, Spinner, Row, Col } from "reactstrap";
import Session from "../auth/Session";
import QboPullPreviewModal, { QBO_PULL_SECTIONS } from "./QboPullPreviewModal";

function authHeaders() {
    const token = Session.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
}

function formatUtc(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return String(value);
    }
}

function QuickBooksPanel() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reconcile, setReconcile] = useState(null);
    const [error, setError] = useState(null);
    // Which entity grouping's pull-preview modal is open (null = none).
    const [previewSection, setPreviewSection] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            const resp = await fetch('api/qbo/status', { headers: authHeaders() });
            if (!resp.ok) throw new Error(`status ${resp.status}`);
            setStatus(await resp.json());
            setError(null);
        } catch (e) {
            setError(`Failed to load QBO status: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchReconcileStatus = useCallback(async () => {
        try {
            const resp = await fetch('api/qbo/reconcile/status', { headers: authHeaders() });
            if (!resp.ok) return;
            setReconcile(await resp.json());
        } catch {
            // ignore transient
        }
    }, []);

    useEffect(() => {
        fetchStatus();
        fetchReconcileStatus();
    }, [fetchStatus, fetchReconcileStatus]);

    useEffect(() => {
        // poll while reconciliation is running
        if (!reconcile?.running) return undefined;
        const id = setInterval(fetchReconcileStatus, 2000);
        return () => clearInterval(id);
    }, [reconcile?.running, fetchReconcileStatus]);

    const onConnect = async () => {
        try {
            const resp = await fetch('api/qbo/connect', { headers: authHeaders() });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            if (data?.authorizeUrl) {
                window.location.href = data.authorizeUrl;
            }
        } catch (e) {
            setError(`Connect failed: ${e.message}`);
        }
    };

    const onDisconnect = async () => {
        if (!window.confirm("Disconnect QuickBooks? Tokens will be cleared; you'll need to reconnect to sync.")) return;
        try {
            const resp = await fetch('api/qbo/disconnect', { method: 'POST', headers: authHeaders() });
            if (!resp.ok && resp.status !== 204) throw new Error(`HTTP ${resp.status}`);
            await fetchStatus();
        } catch (e) {
            setError(`Disconnect failed: ${e.message}`);
        }
    };

    const onReconcile = async () => {
        try {
            const resp = await fetch('api/qbo/reconcile', { method: 'POST', headers: authHeaders() });
            if (!resp.ok && resp.status !== 202) throw new Error(`HTTP ${resp.status}`);
            await fetchReconcileStatus();
        } catch (e) {
            setError(`Reconciliation trigger failed: ${e.message}`);
        }
    };

    if (loading) {
        return <div><Spinner size="sm" /> Loading QuickBooks status…</div>;
    }

    const connected = status?.connected === true;
    const dryRun = status?.dryRun === true;

    return (
        <div>
            <h3>QuickBooks Online</h3>
            {error && <div className="alert alert-danger">{error}</div>}

            <Row>
                <Col md={6}>
                    <Card className="mb-3">
                        <CardBody>
                            <CardTitle tag="h5">Connection</CardTitle>
                            <p>
                                Status:{" "}
                                {connected
                                    ? <Badge color="success">Connected</Badge>
                                    : <Badge color="secondary">Not connected</Badge>}
                                {dryRun && (
                                    <span className="ms-2">
                                        <Badge color="warning">Dry-Run</Badge>
                                    </span>
                                )}
                            </p>
                            {connected && (
                                <>
                                    <div><strong>Realm:</strong> {status.realmId || "—"}</div>
                                    <div><strong>Connected:</strong> {formatUtc(status.connectedAtUtc)}</div>
                                    <div><strong>Access token expires:</strong> {formatUtc(status.accessTokenExpiresAtUtc)}</div>
                                    <div><strong>Refresh token expires:</strong> {formatUtc(status.refreshTokenExpiresAtUtc)}</div>
                                </>
                            )}
                            <div className="mt-3">
                                {connected
                                    ? <Button color="danger" outline onClick={onDisconnect}>Disconnect</Button>
                                    : <Button color="primary" onClick={onConnect}>Connect to QuickBooks</Button>}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="mb-3">
                        <CardBody>
                            <CardTitle tag="h5">Sync activity</CardTitle>
                            <div><strong>Outbound enabled:</strong> {status?.outboundEnabled ? "Yes" : "No"}</div>
                            <div><strong>Inbound (CDC) enabled:</strong> {status?.inboundEnabled ? "Yes" : "No"}</div>
                            <div><strong>Last CDC timestamp:</strong> {formatUtc(status?.lastCdcTimestampUtc)}</div>
                            <div><strong>Last CDC error:</strong> {status?.lastCdcError || "—"}</div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Card className="mb-3">
                <CardBody>
                    <CardTitle tag="h5">Preview QuickBooks pull</CardTitle>
                    <p className="text-muted">
                        Review and link existing QuickBooks records to local records, one grouping
                        at a time. Each preview lets you skip records you don't want to link, and
                        skipped records stay hidden on future previews unless you choose to show them.
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                        {QBO_PULL_SECTIONS.map(s => (
                            <Button
                                key={s.key}
                                color="secondary"
                                outline
                                onClick={() => setPreviewSection(s.key)}
                                disabled={!connected}
                            >
                                {s.label}
                            </Button>
                        ))}
                    </div>
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardBody>
                    <CardTitle tag="h5">Reconciliation</CardTitle>
                    <p className="text-muted">
                        Walks all Customers, SubCustomers, and active Drivers that have no QuickBooks id yet.
                        Matches by exact name; creates in QuickBooks if not found. Idempotent.
                    </p>
                    <Button
                        color="primary"
                        onClick={onReconcile}
                        disabled={reconcile?.running || !connected || !status?.reconciliationEnabled}
                        title={status?.reconciliationEnabled ? undefined : "Disabled by configuration"}
                    >
                        {reconcile?.running ? "Running…" : "Run reconciliation"}
                    </Button>
                    {reconcile && (
                        <div className="mt-3">
                            <div>
                                <strong>Status:</strong>{" "}
                                {reconcile.running ? <Badge color="info">Running</Badge> : <Badge color="secondary">Idle</Badge>}
                            </div>
                            <div><strong>Mode:</strong> {reconcile.mode || "â€”"}</div>
                            <div><strong>Progress:</strong> {reconcile.processed} / {reconcile.total}</div>
                            <div><strong>Created:</strong> {reconcile.created || 0}</div>
                            <div><strong>Updated:</strong> {reconcile.updated || 0}</div>
                            <div><strong>Started:</strong> {formatUtc(reconcile.startedAtUtc)}</div>
                            <div><strong>Finished:</strong> {formatUtc(reconcile.finishedAtUtc)}</div>
                            {reconcile.lastErrorMessage && (
                                <div className="text-danger"><strong>Last error:</strong> {reconcile.lastErrorMessage}</div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            <QboPullPreviewModal
                isOpen={previewSection !== null}
                sectionKey={previewSection}
                toggle={() => setPreviewSection(null)}
            />
        </div>
    );
}

export default QuickBooksPanel;
